import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FaceIcon from "@mui/icons-material/Face";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SearchIcon from "@mui/icons-material/Search";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import Box from "@mui/joy/Box";
import CircularProgress from "@mui/joy/CircularProgress";
import Divider from "@mui/joy/Divider";
import Dropdown from "@mui/joy/Dropdown";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton from "@mui/joy/ListItemButton/ListItemButton";
import listItemButtonClasses from "@mui/joy/ListItemButton/listItemButtonClasses";
import ListItemContent from "@mui/joy/ListItemContent";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import ListSubheader from "@mui/joy/ListSubheader";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalOverflow from "@mui/joy/ModalOverflow";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import debounce from "lodash/debounce";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../../../features/admin/providers/UserProvider";
import { Permission, hasPermission } from "../../../models/auth";
import { UnixTimestamp } from "../../../models/common";
import { Person } from "../../../models/person";
import { ReactComponent as Logo } from "../../assets/resilience-logo.svg";
import { PersonQuartzClientContext } from "../../services/api/contexts";
import { unixToDayjs } from "../../utils/time";

interface PatientSearchModalProps {
  open: boolean;
  onClose: () => void;
  navigate: (path: string) => void;
}

// Modal component to search for patients
const PatientSearchModal = ({ open, onClose, navigate }: PatientSearchModalProps) => {
  const client = React.useContext(PersonQuartzClientContext);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [results, setResults] = React.useState<Person[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const handler = debounce(async (term: string) => {
      try {
        const bundle = await client.listPersons(50, 0, term);
        setResults(bundle.entry as Person[]);
      } catch (e: any) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, 300);

    if (searchTerm.trim()) {
      setLoading(true);
      handler(searchTerm.trim());
    } else {
      setResults([]);
    }

    return () => {
      handler.cancel();
    };
  }, [searchTerm, client]);

  const formatDate = (dateString?: UnixTimestamp | null) => {
    if (!dateString) return "-";
    try {
      return unixToDayjs(dateString)?.format("DD MMM YYYY");
    } catch {
      return dateString;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalOverflow>
        <ModalDialog sx={{ minWidth: 400 }}>
          <ModalClose />
          <Typography level="h4">Search Patient</Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Input
              startDecorator={<SearchIcon />}
              placeholder="Enter patient name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <List size="sm">
                {results.map((p) => (
                  <ListItem key={p.person_id}>
                    <ListItemButton
                      onClick={() => {
                        navigate(`/patient/${p.person_id}`);
                        onClose();
                      }}
                    >
                      <ListItemDecorator>
                        <PersonOutlineIcon />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-md" fontWeight="md">
                          {p.given_name} {p.family_name}
                        </Typography>
                        <Typography level="body-xs">
                          DOB: {formatDate(p.birth_date)}, Gender: {p.gender || "-"}
                        </Typography>
                        {p.external_ids?.map((id) => (
                          <Typography key={`${id.system}-${id.value}`} level="body-xs">
                            {id.system}: {id.value}
                          </Typography>
                        ))}
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
};

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = React.useContext(UserContext);
  const [openSearch, setOpenSearch] = React.useState(false);
  const isPatientRoute = location.pathname.includes("/patient");
  const isDocumentRoute = location.pathname.includes("/document");

  return (
    <Stack
      sx={{
        position: "fixed",
        height: "100vh",
        borderRight: "1px solid",
        borderRightColor: "divider",
        backgroundColor: "background.surface",
      }}
      direction="column"
      justifyContent="space-between"
    >
      <Box
        sx={{
          width: "240px",
          flexShrink: 0,
          p: 2,
        }}
      >
        {user && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="column">
              <Typography level="title-lg">
                {user.attributes.given_name + " " + user.attributes.name[0] + "."}
              </Typography>
              <Typography level="body-sm">{user.attributes.organization}</Typography>
            </Stack>
            <Dropdown>
              <MenuButton size="sm" sx={{ width: "32px" }}>
                <ArrowDropDownIcon />
              </MenuButton>

              <Menu>
                <MenuItem
                  onClick={() => {
                    navigate("/user/profile");
                  }}
                >
                  <FaceIcon fontSize="inherit" />
                  Profile
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate("/auth/logout");
                  }}
                >
                  <LogoutIcon fontSize="inherit" />
                  Logout
                </MenuItem>
              </Menu>
            </Dropdown>
          </Stack>
        )}

        <Divider sx={{ mt: 2 }} />

        <Box
          sx={{
            mt: 2,
            minHeight: 0,
            overflow: "hidden auto",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            [`& .${listItemButtonClasses.root}`]: {
              gap: 1.5,
            },
          }}
        >
          <List
            size="sm"
            sx={{
              gap: 1,
              "--ListItem-radius": (theme) => theme.vars.radius.sm,
            }}
          >
            {/* Patients section - only show if user has PATIENT permission */}
            {user && hasPermission(user.attributes, Permission.PATIENT) && (
              <>
                <ListItem
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    ...(isPatientRoute ? { background: "#e6edf8" } : {}),
                  }}
                >
                  <ListItemButton onClick={() => navigate("/patients")}>
                    <ListItemDecorator>
                      <PersonOutlineIcon />
                    </ListItemDecorator>
                    <ListItemContent>Patients</ListItemContent>
                  </ListItemButton>
                  <IconButton size="sm" onClick={() => setOpenSearch(true)}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </ListItem>
                <ListItem
                  sx={{
                    ...(location.pathname.includes("/note") ? { background: "#e6edf8" } : {}),
                  }}
                >
                  <ListItemButton onClick={() => navigate("/notes")}>
                    <ListItemDecorator>
                      <NotesOutlinedIcon />
                    </ListItemDecorator>
                    <ListItemContent>Notes</ListItemContent>
                  </ListItemButton>
                </ListItem>
              </>
            )}

            {/* Documents section - only show if user has DOCUMENT permission */}
            {user && hasPermission(user.attributes, Permission.DOCUMENT) && (
              <ListItem
                sx={{
                  ...(isDocumentRoute ? { background: "#e6edf8" } : {}),
                }}
              >
                <ListItemButton onClick={() => navigate("/documents")}>
                  <ListItemDecorator>
                    <DescriptionOutlinedIcon />
                  </ListItemDecorator>
                  <ListItemContent>Documents</ListItemContent>
                </ListItemButton>
              </ListItem>
            )}

            {/* RPM section - only show if user has RPM permission */}
            {user && hasPermission(user.attributes, Permission.RPM) && (
              <ListItem nested>
                <ListSubheader sticky>Resilience RPM</ListSubheader>
                <ListItem
                  sx={{
                    ...(location.pathname.includes("/rpm") ? { background: "#e6edf8" } : {}),
                  }}
                >
                  <ListItemButton onClick={() => navigate("/rpm/inclusion")}>
                    <ListItemDecorator>
                      <GroupOutlinedIcon />
                    </ListItemDecorator>
                    <ListItemContent>Inclusion</ListItemContent>
                  </ListItemButton>
                </ListItem>
              </ListItem>
            )}

            {/* Research section - only show if user has COHORT or PROJECT permissions */}
            {user &&
              (hasPermission(user.attributes, Permission.COHORT) ||
                hasPermission(user.attributes, Permission.PROJECT)) && (
                <ListItem nested>
                  <ListSubheader sticky>Research</ListSubheader>
                  {hasPermission(user.attributes, Permission.COHORT) && (
                    <ListItem
                      sx={{
                        ...(location.pathname.includes("/cohort") ? { background: "#e6edf8" } : {}),
                      }}
                    >
                      <ListItemButton onClick={() => navigate("/cohorts")}>
                        <ListItemDecorator>
                          <GroupOutlinedIcon />
                        </ListItemDecorator>
                        <ListItemContent>Cohorts</ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}
                  {hasPermission(user.attributes, Permission.PROJECT) && (
                    <ListItem
                      sx={{
                        ...(location.pathname.includes("/stud") ? { background: "#e6edf8" } : {}),
                      }}
                    >
                      <ListItemButton onClick={() => navigate("/studies")}>
                        <ListItemDecorator>
                          <FolderOutlinedIcon />
                        </ListItemDecorator>
                        <ListItemContent>Projects</ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}
                </ListItem>
              )}

            {/* Admin section - only show on onprem and if user has ADMIN permission */}
            {window.CONFIG.auth.kind === "SIMPLE" &&
            user &&
            hasPermission(user.attributes, Permission.ADMIN) ? (
              <ListItem nested>
                <ListSubheader sticky>Admin</ListSubheader>
                <ListItem
                  sx={{
                    ...(location.pathname === "/admin" ? { background: "#e6edf8" } : {}),
                  }}
                >
                  <ListItemButton onClick={() => navigate("/admin")}>
                    <ListItemDecorator>
                      <SettingsSuggestIcon />
                    </ListItemDecorator>
                    <ListItemContent>Admin panel</ListItemContent>
                  </ListItemButton>
                </ListItem>
              </ListItem>
            ) : (
              <></>
            )}
          </List>
        </Box>
      </Box>
      <Box
        sx={{
          textAlign: "center",
          width: "100%",
          padding: "16px 0",
        }}
      >
        <Logo />
        {/* <img alt="Resilience" src={logo} /> */}
      </Box>
      <PatientSearchModal
        open={openSearch}
        onClose={() => setOpenSearch(false)}
        navigate={navigate}
      />
    </Stack>
  );
};
